import { useState, useCallback } from "react"
import {
  RefreshCw, ChevronDown, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, MinusCircle,
} from "lucide-react"
import { runAllChecks, type ChecksResponse, type CheckSuite, type CheckResult } from "@/lib/api"

const statusIcon = {
  pass: CheckCircle,
  fail: XCircle,
  warn: AlertTriangle,
  skip: MinusCircle,
}

const statusColor = {
  pass: "text-green-400",
  fail: "text-red-400",
  warn: "text-amber-400",
  skip: "text-gray-500",
}

const badgeBg = {
  pass: "bg-green-500/15 text-green-400",
  fail: "bg-red-500/15 text-red-400",
  warn: "bg-amber-500/15 text-amber-400",
  skip: "bg-gray-500/15 text-gray-500",
}

const overallBorder = {
  pass: "border-green-500/30",
  fail: "border-red-500/30",
  warn: "border-amber-500/30",
}

function CheckRow({ check }: { check: CheckResult }) {
  const Icon = statusIcon[check.status] || MinusCircle
  return (
    <div className="flex items-start gap-3 py-2.5 border-t border-border first:border-t-0">
      <Icon size={16} className={`shrink-0 mt-0.5 ${statusColor[check.status]}`} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-gray-200">{check.name}</div>
        {check.detail && (
          <div className="text-xs text-gray-500 mt-0.5 break-words">{check.detail}</div>
        )}
        {check.error && (
          <div className="text-xs text-red-400 mt-0.5 break-words font-mono">{check.error}</div>
        )}
      </div>
      <span className="text-[10px] text-gray-600 font-mono shrink-0">{check.duration_ms}ms</span>
    </div>
  )
}

function SuiteCard({ suite }: { suite: CheckSuite }) {
  const [open, setOpen] = useState(true)
  const counts = { pass: 0, fail: 0, warn: 0, skip: 0 }
  suite.checks.forEach(c => counts[c.status]++)

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center w-full px-4 py-3 gap-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        {open ? <ChevronDown size={14} className="text-gray-600 shrink-0" /> : <ChevronRight size={14} className="text-gray-600 shrink-0" />}
        <span className="text-sm font-medium text-gray-200 flex-1">{suite.name}</span>
        <div className="flex gap-1.5">
          {counts.pass > 0 && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeBg.pass}`}>{counts.pass} pass</span>}
          {counts.fail > 0 && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeBg.fail}`}>{counts.fail} fail</span>}
          {counts.warn > 0 && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeBg.warn}`}>{counts.warn} warn</span>}
          {counts.skip > 0 && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${badgeBg.skip}`}>{counts.skip} skip</span>}
        </div>
      </button>
      {open && (
        <div className="px-4 pb-3">
          {suite.checks.map((c, i) => <CheckRow key={i} check={c} />)}
        </div>
      )}
    </div>
  )
}

export default function ChecksPanel() {
  const [data, setData] = useState<ChecksResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleRun = useCallback(async () => {
    setLoading(true)
    setError("")
    try {
      const result = await runAllChecks()
      setData(result)
    } catch (e: any) {
      setError(e.message || "Failed to run checks")
    } finally {
      setLoading(false)
    }
  }, [])

  return (
    <div className="space-y-3">
      {/* Run button */}
      <button
        onClick={handleRun}
        disabled={loading}
        className="w-full py-2.5 rounded-lg bg-accent/20 hover:bg-accent/30 text-accent text-sm font-medium transition-colors border border-accent/20 disabled:opacity-50 disabled:cursor-wait"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <RefreshCw size={14} className="animate-spin" />
            Running checks...
          </span>
        ) : (
          data ? "Re-run All Checks" : "Run All Checks"
        )}
      </button>

      {error && (
        <div className="text-xs text-red-400 px-1">{error}</div>
      )}

      {/* Summary */}
      {data && (
        <>
          <div className={`rounded-lg border ${overallBorder[data.overall]} p-3`}>
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <div className="text-lg font-mono text-green-400">{data.total_pass}</div>
                <div className="text-[10px] text-gray-500">Passed</div>
              </div>
              <div>
                <div className="text-lg font-mono text-red-400">{data.total_fail}</div>
                <div className="text-[10px] text-gray-500">Failed</div>
              </div>
              <div>
                <div className="text-lg font-mono text-amber-400">{data.total_warn}</div>
                <div className="text-[10px] text-gray-500">Warnings</div>
              </div>
              <div>
                <div className="text-lg font-mono text-gray-400">{data.duration_ms}</div>
                <div className="text-[10px] text-gray-500">ms</div>
              </div>
            </div>
          </div>

          {/* Suites */}
          {data.suites.map(suite => (
            <SuiteCard key={suite.id} suite={suite} />
          ))}

          {/* Footer meta */}
          <div className="flex gap-3 text-[10px] text-gray-600 px-1">
            <span>Env: {data.environment}</span>
            <span>API: {data.api_url}</span>
            <span>{new Date(data.timestamp * 1000).toLocaleTimeString()}</span>
          </div>
        </>
      )}
    </div>
  )
}
